import {
  ArrowLeft,
  ShieldCheck,
  Smartphone,
  ChevronRight,
  CreditCard,
  Building2,
  Lock
} from 'lucide-react';
import { useProfile } from '../../../../../context/profile/ProfileContext';

const CheckoutDirectModal = () => {
  const {
    subscription,
    showCheckoutPage,
    setShowCheckoutPage,
    selectedUpgradePlan,
    triggerRazorpayMandate,
    formatDate,
    formatCurrency
  } = useProfile();

  if (!showCheckoutPage || !selectedUpgradePlan) return null;

  const cycleEndDate = subscription?.end_date || subscription?.next_billing_at || subscription?.nextBillingAt;
  const formattedDebitDate = cycleEndDate ? formatDate(cycleEndDate) : 'cycle end date';

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-md overflow-y-auto"
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
    >
      <div className="bg-[#FAFAFA] rounded-3xl max-w-md w-full shadow-2xl border border-slate-200 overflow-hidden my-auto animate-modal-entry">
        {/* Top Header Bar */}
        <div className="p-5 pb-3 flex items-center justify-between border-b border-slate-200/60 bg-white">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowCheckoutPage(false)}
              className="w-9 h-9 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-700 hover:bg-slate-100 transition-all cursor-pointer shadow-xs"
            >
              <ArrowLeft size={18} />
            </button>
            <div>
              <h3 className="font-extrabold text-sm text-slate-900">Subscription Mandate Checkout</h3>
              <span className="text-[11px] text-slate-500">Secure e-Mandate Authorization</span>
            </div>
          </div>
          <div className="flex items-center gap-1 bg-teal-50 text-[#067978] px-2.5 py-1 rounded-full text-[11px] font-bold border border-teal-200/80">
            <ShieldCheck size={13} />
            <span>Secure</span>
          </div>
        </div>

        <div className="p-5 pt-4 space-y-4">
          {/* Price & Mandate Summary Card */}
          <div className="bg-white rounded-2xl p-5 border border-slate-200/90 shadow-2xs space-y-3">
            <div className="flex justify-between items-start">
              <div>
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 block">Mandate Verification Fee</span>
                <div className="flex items-baseline gap-2 mt-0.5">
                  <span className="text-3xl font-black text-slate-900">₹5.00</span>
                  <span className="text-[10px] text-emerald-800 font-extrabold bg-emerald-100/90 px-2 py-0.5 rounded-full border border-emerald-300">100% Refundable</span>
                </div>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-teal-50 text-[#067978] flex items-center justify-center shrink-0 border border-teal-100 font-black text-lg shadow-2xs">
                {selectedUpgradePlan.name.charAt(0)}
              </div>
            </div>

            <div className="border-t border-dashed border-slate-200 pt-3 space-y-2 text-xs">
              <div className="flex justify-between items-center text-slate-600">
                <span>Target Plan:</span>
                <b className="text-slate-900 font-bold">{selectedUpgradePlan.name} Tier</b>
              </div>
              <div className="flex justify-between items-center text-slate-600">
                <span>Next cycle recurring rate:</span>
                <b className="text-slate-900 font-bold">{formatCurrency(selectedUpgradePlan.monthly_price)} / 30 days (+18% GST)</b>
              </div>
              <div className="flex justify-between items-center p-2.5 rounded-xl bg-teal-50/80 border border-teal-200/80">
                <span className="text-teal-900 font-bold">First automated debit date:</span>
                <b className="text-[#067978] font-extrabold text-xs bg-white px-2.5 py-1 rounded-md border border-teal-200 shadow-2xs">
                  {formattedDebitDate}
                </b>
              </div>
            </div>
          </div>

          {/* Mandate Payment Options */}
          <div>
            <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500 block mb-2.5 px-1">
              PAYMENT OPTIONS FOR MANDATE
            </span>
            <div className="space-y-2.5">
              {/* UPI Option */}
              <div
                onClick={() => triggerRazorpayMandate('upi')}
                className="bg-white hover:bg-teal-50/50 p-4 rounded-2xl border border-slate-200/90 flex items-center justify-between cursor-pointer transition-all shadow-2xs group"
              >
                <div className="flex items-center gap-3.5">
                  <div className="w-11 h-11 rounded-xl bg-teal-50 text-[#067978] flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform border border-teal-100">
                    <Smartphone size={20} />
                  </div>
                  <div>
                    <h5 className="font-bold text-sm text-slate-900">UPI Autopay / Mandate</h5>
                    <p className="text-xs text-slate-500">Google Pay, PhonePe, Paytm, BHIM</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">Recommended</span>
                  <ChevronRight size={18} className="text-slate-400 group-hover:text-[#067978] group-hover:translate-x-0.5 transition-all" />
                </div>
              </div>

              {/* Debit / Credit Cards Option */}
              <div
                onClick={() => triggerRazorpayMandate('card')}
                className="bg-white hover:bg-teal-50/50 p-4 rounded-2xl border border-slate-200/90 flex items-center justify-between cursor-pointer transition-all shadow-2xs group"
              >
                <div className="flex items-center gap-3.5">
                  <div className="w-11 h-11 rounded-xl bg-teal-50 text-[#067978] flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform border border-teal-100">
                    <CreditCard size={20} />
                  </div>
                  <div>
                    <h5 className="font-bold text-sm text-slate-900">Debit / Credit Card Mandate</h5>
                    <p className="text-xs text-slate-500">Visa, Mastercard, RuPay e-Mandate</p>
                  </div>
                </div>
                <ChevronRight size={18} className="text-slate-400 group-hover:text-[#067978] group-hover:translate-x-0.5 transition-all" />
              </div>

              {/* Netbanking Option */}
              <div
                onClick={() => triggerRazorpayMandate('netbanking')}
                className="bg-white hover:bg-teal-50/50 p-4 rounded-2xl border border-slate-200/90 flex items-center justify-between cursor-pointer transition-all shadow-2xs group"
              >
                <div className="flex items-center gap-3.5">
                  <div className="w-11 h-11 rounded-xl bg-teal-50 text-[#067978] flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform border border-teal-100">
                    <Building2 size={20} />
                  </div>
                  <div>
                    <h5 className="font-bold text-sm text-slate-900">Netbanking e-Mandate</h5>
                    <p className="text-xs text-slate-500">HDFC, ICICI, SBI, Axis &amp; Indian banks</p>
                  </div>
                </div>
                <ChevronRight size={18} className="text-slate-400 group-hover:text-[#067978] group-hover:translate-x-0.5 transition-all" />
              </div>
            </div>
          </div>

          {/* Security & Trust Footer */}
          <div className="p-3.5 rounded-2xl bg-teal-50/60 border border-teal-100 flex items-center justify-between text-xs text-slate-600">
            <div className="flex items-center gap-2">
              <Lock size={15} className="text-[#067978]" />
              <span>Encrypted &amp; Secured by <b>Razorpay</b></span>
            </div>
            <span className="text-[10px] font-bold text-teal-800 bg-white px-2 py-0.5 rounded border border-teal-200">RBI Mandate Compliant</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutDirectModal;
